import { adfModelTypeMap } from './../consts/adf-model-type-map-const';
import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, cosmosSqlAuthMethod, CredentialTypeOptions } from 'shell-models-lib';
import { cosmosDBPEWellknownErrorMessages, cosmosDocumentDomain } from 'app/shared/utils/connector-metadata-static-consts';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { GlobalVariables } from 'app/services/globals';
import { mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { dataflowCosmosDbSink, dataflowCosmosDbSource } from 'app/registries/dataflow/cosmos-db-dataflow-metadata';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { compact } from 'lodash';

const cosmosSqlEndpointSettings = (fetchAccountKey: boolean = false, isAccountSelectOnly: boolean = true): ControlMetadata => ({
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AccountSelectionMethodLabel,
    description: stringResources.AzureCosmos_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    defaultValue: '{accountEndpoint != null || parameterized ? "manual" : "azure"}',
    isLabelVertical: true,
    cases: [
        {
            caseValue: 'azure',
            children: ([
                {
                    type: 'azure-cosmos-db-select',
                    fetchAccountKey,
                    isAccountSelectOnly,
                    bind: fetchAccountKey ? (
                        isAccountSelectOnly ?
                            {
                                accountEndpoint: 'accountEndpoint',
                                accessKey: 'accessKey',
                                resourceID: '_accountResourceID'
                            } :
                            {
                                accountEndpoint: 'accountEndpoint',
                                accessKey: 'accessKey',
                                databaseName: 'databaseName',
                                resourceID: '_accountResourceID'
                            }
                    ) : (
                        isAccountSelectOnly ?
                            {
                                accountEndpoint: 'accountEndpoint',
                                resourceID: '_accountResourceID'
                            } :
                            {
                                accountEndpoint: 'accountEndpoint',
                                databaseName: 'databaseName',
                                resourceID: '_accountResourceID'
                            }
                    ),
                    showPEStatus: true,
                    validators: {
                        accountEndpoint: [
                            { type: 'required', errorMessage: '' }
                        ],
                        accessKey: fetchAccountKey ? [
                            { type: 'required', errorMessage: '' }
                        ] : [],
                        databaseName: isAccountSelectOnly ? [] : [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                }
            ] as ControlMetadata[]).concat(isAccountSelectOnly ? [
                {
                    type: 'parameterizable',
                    child:
                    {
                        type: 'textbox',
                        bind: 'databaseName',
                        label: stringResources.ModelName_DatabaseName,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                }
            ] : [])
        },
        {
            caseValue: 'manual',
            children: ([
                {
                    type: 'parameterizable',
                    child:
                    {
                        type: 'textbox',
                        bind: 'accountEndpoint',
                        label: stringResources.ModelName_AzureCosmosAccountURI,
                        required: true,
                        placeholder: `https://{accountEndpoint}.${cosmosDocumentDomain}:443/`,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                }
            ] as ControlMetadata[]).concat(fetchAccountKey ? [
                {
                    type: 'dynamic-akv-switcher',
                    enableAKVParameterization: true,
                    hideAKVSwitcherInConnStrProps: true,
                    bind: 'accessKey',
                    label: stringResources.ModelName_AzureCosmosAccountKey,
                    child: {
                        type: 'parameterizable',
                        attribute: 'accessKey',
                        child:
                        {
                            type: 'password',
                            label: stringResources.ModelName_AzureCosmosAccountKey,
                            required: true,
                            isLabelVertical: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ]
                        },
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.CosmosDb_DisplayText),
                            stringResources.ModelName_AzureCosmosAccountKey)
                    }
                }
            ] : []).concat(
                {
                    type: 'parameterizable',
                    child:
                    {
                        type: 'textbox',
                        bind: 'databaseName',
                        label: stringResources.ModelName_DatabaseName,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'private-endpoint-status'
                }
            )
        }
    ]
});

const authMethods: Array<ISelectOption<string>> = [
    {
        label: stringResources.AuthenticationType_AccountKey,
        value: cosmosSqlAuthMethod.connectionString
    },
    {
        label: stringResources.AuthenticationType_ServicePrincipal,
        value: cosmosSqlAuthMethod.servicePrincipal
    },
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: cosmosSqlAuthMethod.managedIdentity
    },
    {
        label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
        value: cosmosSqlAuthMethod.userAssigedManagedIdentity
    }
];

export const cosmosDbMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.cosmosSql.linkedServiceType,
        displayName: stringResources.CosmosDb_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-AzureCosmosDB'
        },
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'CosmosDB' as ResourceKind
        },
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.Key,
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.WorkspaceIdentity
        ]),
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        tableNameAlias: 'collectionName',
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        dataflowSource: [dataflowCosmosDbSource],
        dataflowSink: [dataflowCosmosDbSink],
        supportPE: true,
        peGroupIDs: ['Sql', 'Analytical'],
        resourceTypes: ['Microsoft.DocumentDb/databaseAccounts'],
        peWellknowErrorMessages: cosmosDBPEWellknownErrorMessages,
        isFirstClass: true,
        supportDatasetV2: true,
        supportOPDG: GlobalVariables.enableOPDG,
        linkedServiceNamePrefix: 'CosmosDbNoSql',
        copySinkTypes: [adfModelTypeMap.cosmosSql.copySinkType],
        copySourceTypes: [adfModelTypeMap.cosmosSql.copySourceType],
        category: ['Azure'],
        onetimeQueryTemplate: 'select * from c',
        // eslint-disable-next-line max-len
        timedQueryTemplate: 'select * from c \nwhere c.<<columnName>> >= \'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-ddTHH:mm:ssZ\' )}\' \nAND c.<<columnName>> < \'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-ddTHH:mm:ssZ\' )}\'',
        deltaLoadQueryTemplate: 'select * from c where c.<<columnName>> > <<startValue>> and c.<<columnName>> <= <<endValue>>',
        supportFirstClassParameterExperience: true,
        controls: [
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                isLabelVertical: true,
                label: stringResources.ModelName_AuthenticationType,
                options: mergeUserAuthCredentialOption(authMethods, cosmosSqlAuthMethod.userAuth),
                cases: [
                    {
                        caseValue: cosmosSqlAuthMethod.connectionString,
                        children: [
                            {
                                type: 'select-button-switch',
                                required: true,
                                isLabelVertical: true,
                                options: [
                                    {
                                        label: stringResources.ModelName_ConnectionString,
                                        value: false
                                    },
                                    {
                                        label: stringResources.AzureKeyVault_DisplayText,
                                        value: true,
                                        hideAKVOptionIfNotChosen: false
                                    }
                                ],
                                bind: 'enableAKVForConnectionString',
                                isNotRelatedToCredential: true,
                                defaultValue: false,
                                cases: [
                                    {
                                        caseValue: true,
                                        children: [
                                            {
                                                type: 'azure-key-vault',
                                                enableAKVParameterization: true,
                                                bind: 'referedAKVForConnectionString',
                                                isLabelVertical: true,
                                                description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                                                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.CosmosDb_DisplayText),
                                                    'AccountEndpoint=<EndpointUrl>;AccountKey=<AccessKey>;Database=<Database>')
                                            }
                                        ]
                                    },
                                    {
                                        caseValue: false,
                                        children: [
                                            cosmosSqlEndpointSettings(true, false),
                                            {
                                                type: 'key-val-list',
                                                bind: 'additionalFields',
                                                label: stringResources.AdditionalConnectionStringFields
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        caseValue: cosmosSqlAuthMethod.servicePrincipal,
                        children: [
                            cosmosSqlEndpointSettings(),
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'tenant-textbox',
                                    bind: 'tenant',
                                    validators: [
                                        { type: 'required', errorMessage: '' }
                                    ],
                                    isLabelVertical: true,
                                    label: stringResources.ModelName_Tenant,
                                    required: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'servicePrincipalId',
                                    label: stringResources.ModelName_ServicePrincipalID,
                                    required: true,
                                    validators: [
                                        { type: 'required', errorMessage: '' }
                                    ],
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'radio-switch',
                                bind: 'servicePrincipalCredentialType',
                                nested: 1,
                                label: stringResources.ModelName_ServicePrincipalCredentialType,
                                isLabelVertical: true,
                                options: [
                                    {
                                        label: stringResources.ModelName_ServicePrincipalKey,
                                        value: 'ServicePrincipalKey'
                                    },
                                    {
                                        label: stringResources.ModelName_ServicePrincipalCert,
                                        value: 'ServicePrincipalCert'
                                    }
                                ],
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                defaultValue: 'ServicePrincipalKey',
                                cases: [
                                    {
                                        caseValue: 'ServicePrincipalKey',
                                        children: [
                                            {
                                                type: 'dynamic-akv-switcher',
                                                bind: 'servicePrincipalKey',
                                                label: stringResources.ModelName_ServicePrincipalKey,
                                                child: {
                                                    type: 'password',
                                                    label: stringResources.ModelName_ServicePrincipalKey,
                                                    required: true,
                                                    isLabelVertical: true
                                                },
                                                validators: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Dynamics_DisplayText),
                                                    stringResources.ModelName_ServicePrincipalKey)
                                            }
                                        ]
                                    },
                                    {
                                        caseValue: 'ServicePrincipalCert',
                                        children: [
                                            {
                                                type: 'dynamic-akv-switcher',
                                                bind: 'servicePrincipalCert',
                                                label: stringResources.ModelName_ServicePrincipalCert,
                                                validators: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                akvOnly: true,
                                                isCert: true,
                                                akvOptionDisplayText: stringResources.AzureKeyVault_ServicePrincipalCert_DisplayText,
                                                description: format(stringResources.AzureKeyVaultCertName_Tooltip_Template,
                                                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Dynamics_DisplayText),
                                                    stringResources.ModelName_ServicePrincipalCert)
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        caseValue: cosmosSqlAuthMethod.managedIdentity,
                        children: [
                            cosmosSqlEndpointSettings(),
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.CosmosDb_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-cosmos-db?tabs=data-factory#managed-identity'
                            }
                        ]
                    },
                    {
                        caseValue: cosmosSqlAuthMethod.userAssigedManagedIdentity,
                        children: [
                            cosmosSqlEndpointSettings(),
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        ]
                    },
                    {
                        caseValue: cosmosSqlAuthMethod.userAuth,
                        children: [
                            cosmosSqlEndpointSettings(),
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                hide: !GlobalVariables.enableConnectorUserAuthCred,
                                filterCredentialType: CredentialTypeOptions.UserAuth,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        ]
                    }
                ]
            }
        ]
    };

    return metadata;
};
